import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI, userAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import CardCuttingModal from './CardCuttingModal';
import './RaubGameRoom.css';
import '../styles/game-shell.css';

interface RaubGame {
  id: string;
  name: string;
  hostId: string;
  hostName: string;
  type: 'public' | 'private' | 'friends';
  gameVariant: 'raub';
  status: 'waiting' | 'playing' | 'finished';
  maxPlayers: number;
  currentPlayers: number;
  players: string[];
  spectators: string[];
  inviteCode?: string;
  createdAt: string;
}

type Suit = 'acorn' | 'ball' | 'heart' | 'leaf';

interface HungarianCard {
  id: string;
  rank: number | string;
  suit: Suit;
  image: string;
}

interface RaubPlayer {
  id: string;
  name: string;
  cards: HungarianCard[];
  isDealer: boolean;
  isActive: boolean;
  position: number;
  score: number;
  tricksWon: number;
}

interface CurrentTrick {
  cards: { playerId: string; card: HungarianCard }[];
  leadPlayerId: string;
  leadSuit: Suit;
  winnerId: string | null;
}

interface RaubGameState {
  id: string;
  players: RaubPlayer[];
  dealerIndex: number;
  currentPlayerIndex: number;
  deck: HungarianCard[];
  gamePhase: 'waiting' | 'dealer-decision' | 'adut-determination' | 'card-exchange' | 'mus-ready' | 'mus-discard' | 'playing' | 'hand-finished' | 'game-over';
  winner: string | null;
  trumpSuit: Suit | null;
  proposedCards: HungarianCard[];
  currentProposalRound: number;
  askingPlayerIndex: number;
  dealerHasLooked: boolean;
  raubPlayerId: string | null;
  raubObligationTricks: number;
  autoRaub: boolean;
  discardCounts: Record<string, number | null>;
  exchangeOrder: string[];
  firstFullExchangeDone: boolean;
  faceUpExchangeCard: HungarianCard | null;
  discardedCards: Record<string, HungarianCard[]>;
  awaitingDiscard: string | null;
  currentTrick: CurrentTrick | null;
  completedTricks: any[];
  trickNumber: number;
  playerScores: Record<string, number>;
  isRefa: boolean;
  refaCount: number;
  refaMultiplier: number;
  sevenExchangeAvailable: boolean;
  raubedCard: HungarianCard | null;
  currentMatch: number;
  musMode: boolean;
  musCard: HungarianCard | null;
  musRevealed: boolean;
}

const SUIT_ICONS: Record<Suit, string> = {
  acorn: '♣',
  ball: '⚫',
  heart: '♥',
  leaf: '🍃',
};

const SUIT_COLORS: Record<Suit, string> = {
  acorn: '#228B22',
  ball: '#DAA520',
  heart: '#DC143C',
  leaf: '#2E8B57',
};

const RaubGameRoom: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [game, setGame] = useState<RaubGame | null>(null);
  const [gameState, setGameState] = useState<RaubGameState | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCardCuttingModal, setShowCardCuttingModal] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(isMobile);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(isMobile);

  // Card exchange state
  const [selectedDiscardIndices, setSelectedDiscardIndices] = useState<number[]>([]);
  const [discardCount, setDiscardCount] = useState<number | null>(null);
  const [exchangeStep, setExchangeStep] = useState<'choose-count' | 'select-cards'>('choose-count');

  // Player user data for names
  const [playerUsers, setPlayerUsers] = useState<Record<string, any>>({});
  const voteCardTapRef = useRef(0);

  const loadGame = useCallback(async () => {
    try {
      const response = await gamesAPI.getById(gameId!);
      setGame(response.data);
      await loadPlayerUsers(response.data.players);
    } catch (err) {
      console.error('Error loading Raub game:', err);
      setError('Failed to load game');
    }
  }, [gameId]);

  const loadPlayerUsers = async (playerIds: string[]) => {
    try {
      const results = await Promise.all(
        playerIds.map(async (pid) => {
          try {
            const res = await userAPI.getById(pid);
            return { [pid]: res.data };
          } catch { return null; }
        })
      );
      const map: Record<string, any> = {};
      results.forEach(r => { if (r) Object.assign(map, r); });
      setPlayerUsers(prev => ({ ...prev, ...map }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!gameId || !user) return;

    let socketRef: Socket | null = null;

    const initializeGame = async () => {
      try {
        const gameResponse = await gamesAPI.getById(gameId);
        const gameData = gameResponse.data;
        setGame(gameData);
        await loadPlayerUsers(gameData.players);

        if (gameData.hostId === user.id && gameData.status === 'waiting') {
          const isInGame = gameData.players.includes(user.id) || gameData.spectators?.includes(user.id);
          if (!isInGame) {
            try { await gamesAPI.join(gameId, false); } catch (e) { /* already joined */ }
          }
          if (!gameData.players.includes(user.id)) {
            try {
              await gamesAPI.sit(gameId);
              const refreshed = await gamesAPI.getById(gameId);
              setGame(refreshed.data);
            } catch (e) { /* already sitting */ }
          }
        }

        const token = localStorage.getItem('token');
        if (token) {
          const newSocket = io('/', { auth: { token } });
          socketRef = newSocket;

          newSocket.on('connect', () => {
            newSocket.emit('joinGameRoom', { gameId });
            if (gameData.status === 'playing') {
              newSocket.emit('getGameState', { gameId });
            }
          });

          newSocket.on('gameStateUpdated', (data) => {
            if (data.gameId === gameId) setGameState(data.gameState);
          });

          newSocket.on('gameStateReceived', (data) => {
            if (data.gameId === gameId) setGameState(data.gameState);
          });

          newSocket.on('gameUpdate', (data: any) => {
            if (data.gameId === gameId) loadGame();
          });

          newSocket.on('playerJoined', () => loadGame());
          newSocket.on('playerSat', () => loadGame());
          newSocket.on('playerLeft', () => loadGame());

          newSocket.on('gameStarted', (data) => {
            if (data.gameState) setGameState(data.gameState);
            loadGame();
          });

          newSocket.on('gameError', (data) => {
            setError(data.message);
            setTimeout(() => setError(null), 5000);
          });

          setSocket(newSocket);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error initializing Raub game:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    };

    initializeGame();

    return () => {
      if (socketRef) socketRef.disconnect();
    };
  }, [gameId, user, loadGame]);

  // Reset exchange UI when phase changes
  useEffect(() => {
    if (gameState?.gamePhase !== 'card-exchange') {
      setSelectedDiscardIndices([]);
      setDiscardCount(null);
      setExchangeStep('choose-count');
    }
  }, [gameState?.gamePhase]);

  // Derived values
  const maxPlayersForGame = game?.maxPlayers || 4;
  const myIndex = gameState?.players.findIndex(p => p.id === user?.id) ?? -1;
  const currentPlayer = gameState?.players[myIndex];
  const isMyTurn = gameState ? gameState.currentPlayerIndex === myIndex : false;
  const isHost = game?.hostId === user?.id;
  const askingPlayer = gameState ? gameState.players[gameState.askingPlayerIndex] : null;
  const isMyAsk = askingPlayer?.id === user?.id;
  const amDealer = currentPlayer?.isDealer ?? false;
  const isAwaitingMyDiscard = gameState?.awaitingDiscard === user?.id;
  const musAvailable = !!gameState
    && Object.values(gameState.playerScores || {}).some(s => s < 10)
    && gameState.refaMultiplier === 1
    && !gameState.isRefa;

  const getCardImage = (card: HungarianCard | null | undefined): string => {
    if (!card) return '/assets/back.png';
    if (card.image) return card.image;
    return `/assets/${card.id}.png`;
  };

  // Position current user at bottom, others clockwise
  const getOrderedPlayers = (): RaubPlayer[] => {
    if (!gameState) return [];
    const players = gameState.players;
    if (myIndex < 0) return players;
    const reordered: RaubPlayer[] = [];
    for (let i = 0; i < players.length; i++) {
      reordered.push(players[(myIndex + i) % players.length]);
    }
    return reordered;
  };

  const getPositionStyle = (visualIndex: number, total: number): React.CSSProperties => {
    if (total === 2) {
      return visualIndex === 0
        ? { bottom: '-60px', left: '50%', transform: 'translateX(-50%)' }
        : { top: '-50px', left: '50%', transform: 'translateX(-50%)' };
    }
    if (total === 3) {
      switch (visualIndex) {
        case 0: return { bottom: '-60px', left: '50%', transform: 'translateX(-50%)' };
        case 1: return { top: '50%', right: '-40px', transform: 'translateY(-50%)' };
        case 2: return { top: '-50px', left: '50%', transform: 'translateX(-50%)' };
        default: return {};
      }
    }
    // 4 players
    switch (visualIndex) {
      case 0: return { bottom: '-60px', left: '50%', transform: 'translateX(-50%)' };
      case 1: return { top: '50%', right: '-40px', transform: 'translateY(-50%)' };
      case 2: return { top: '-50px', left: '50%', transform: 'translateX(-50%)' };
      case 3: return { top: '50%', left: '-40px', transform: 'translateY(-50%)' };
      default: return {};
    }
  };

  const handleLeaveRoom = async () => {
    const isActiveGame = game?.status === 'playing';
    const msg = isActiveGame ? t('game.confirmLeaveResign') : t('game.confirmLeave');
    if (!window.confirm(msg)) return;
    try {
      await gamesAPI.leave(gameId!, isActiveGame);
      navigate('/dashboard');
    } catch (err) {
      console.error('Error leaving room:', err);
    }
  };

  // ─── Socket emitters ───────────────────────────────────────────────

  const handleCardCut = async (cutIndex: number) => {
    try {
      await gamesAPI.start(gameId!, cutIndex);
      setShowCardCuttingModal(false);
    } catch (err) {
      console.error('Error cutting cards:', err);
    }
  };

  const handleDealerDecision = (decision: 'raub' | 'pass' | 'mus') => {
    if (socket && gameId) {
      socket.emit('raubDealerDecision', { gameId, decision });
    }
  };

  const handleRevealMus = () => {
    if (socket && gameId) {
      socket.emit('raubRevealMus', { gameId });
    }
  };

  const handleBid = (decision: 'raub' | 'pass') => {
    if (socket && gameId) {
      socket.emit('raubBid', { gameId, decision });
    }
  };

  const handleConfirmDiscard = () => {
    if (socket && gameId) {
      socket.emit('raubSetCardsToDiscard', { gameId, cardIndices: selectedDiscardIndices });
      setSelectedDiscardIndices([]);
      setDiscardCount(null);
      setExchangeStep('choose-count');
    }
  };

  const handleExchangeSeven = (cardIndex: number) => {
    if (socket && gameId) {
      socket.emit('raubExchangeSeven', { gameId, cardIndex });
    }
  };

  const handlePlayCard = (cardIndex: number) => {
    if (socket && gameId) {
      socket.emit('raubPlayCard', { gameId, cardIndex });
    }
  };

  const handleStartNewHand = () => {
    if (socket && gameId) {
      socket.emit('raubStartNewHand', { gameId });
    }
  };

  const toggleDiscardCard = (cardIndex: number) => {
    setSelectedDiscardIndices(prev => {
      if (prev.includes(cardIndex)) {
        return prev.filter(i => i !== cardIndex);
      }
      return [...prev, cardIndex];
    });
  };

  // ─── Render helpers ────────────────────────────────────────────────

  const renderTrumpIndicator = () => {
    if (!gameState?.trumpSuit) return null;
    const suit = gameState.trumpSuit;
    return (
      <div className="raub-trump-indicator">
        <span className="raub-trump-label-inline">Adut:</span>
        <span className="raub-trump-suit" style={{ color: SUIT_COLORS[suit] }}>
          {SUIT_ICONS[suit]} {suit}
        </span>
      </div>
    );
  };

  const renderCurrentTrick = () => {
    if (!gameState?.currentTrick?.cards?.length) return null;
    return (
      <div className="raub-trick-area">
        {gameState.currentTrick.cards.map(({ playerId, card }) => {
          const playerName = gameState.players.find(p => p.id === playerId)?.name || '';
          return (
            <div key={playerId + card.id} className="raub-trick-played-card">
              <img src={getCardImage(card)} alt={card.id} className="raub-trick-card-img" />
              <span className="raub-trick-card-label">{playerName}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPlayerHand = () => {
    if (!currentPlayer) return null;
    const cards = currentPlayer.cards;
    const phase = gameState?.gamePhase;
    const isPlayPhase = phase === 'playing' && isMyTurn;
    const myDiscardCount = gameState?.discardCounts[user?.id || ''];
    const hasSubmitted = myDiscardCount !== null && myDiscardCount !== undefined;
    const isMusDiscard = phase === 'mus-discard' && isAwaitingMyDiscard;
    const isExchangePhase = (phase === 'card-exchange' && (!hasSubmitted || isAwaitingMyDiscard)) || isMusDiscard;

    return (
      <div className="raub-player-hand">
        {cards.map((card, idx) => {
          const isSelected = selectedDiscardIndices.includes(idx);
          let className = 'raub-hand-card-img';
          if (isPlayPhase) className += ' playable';
          if (isExchangePhase) className += isSelected ? ' selected-discard' : ' selectable';

          return (
            <img
              key={card.id + idx}
              src={getCardImage(card)}
              alt={card.id}
              className={className}
              onClick={() => {
                if (isPlayPhase) handlePlayCard(idx);
                else if (isExchangePhase) toggleDiscardCard(idx);
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderWaitingTable = () => (
    <>
      <div className="table-center">
        <h4>{t('game.waitingForPlayers')}</h4>
        <p>{game!.players.length}/{maxPlayersForGame} {t('game.playersJoined')}</p>
        {isHost && <p><small>{t('game.youAreHost')}</small></p>}
      </div>

      {Array.from({ length: maxPlayersForGame }).map((_, seatIndex) => {
        const playerId = game!.players[seatIndex];
        const position = getPositionStyle(seatIndex, maxPlayersForGame);
        const isOccupied = !!playerId;
        const isMe = playerId === user?.id;

        return (
          <div key={seatIndex} className="seat" style={{ position: 'absolute', ...position }}>
            {isOccupied ? (
              <div className={`seat-occupied ${isMe ? 'current-user' : ''}`}>
                <div className="card-placeholder">
                  {Array.from({ length: 5 }).map((_, ci) => (
                    <img
                      key={ci}
                      src="/assets/back.png"
                      alt="Card back"
                      className="card-back"
                      style={{ marginLeft: ci > 0 ? '-15px' : '0' }}
                    />
                  ))}
                </div>
                <div className={`seat-name ${isMe ? 'current-user' : ''}`}>
                  {playerUsers[playerId]?.firstName
                    ? `${playerUsers[playerId].firstName} ${playerUsers[playerId].lastName || ''}`.trim()
                    : gameState?.players.find(p => p.id === playerId)?.name || `Player ${seatIndex + 1}`}
                </div>
              </div>
            ) : (
              <div className="seat-empty">
                <div className="seat-placeholder"><span>Empty Seat</span></div>
                <button
                  className="sit-here-btn"
                  onClick={async () => {
                    try {
                      const isInGame = game!.players.includes(user!.id) || game!.spectators?.includes(user!.id);
                      if (!isInGame) {
                        try { await gamesAPI.join(gameId!, false); } catch (e) { /* already joined */ }
                      }
                      await gamesAPI.sit(gameId!);
                      loadGame();
                    } catch (err) {
                      console.error('Error sitting:', err);
                    }
                  }}
                  disabled={!!game!.players.find(pId => pId === user?.id)}
                >
                  Sit Here
                </button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  const renderProposedSuitLabel = () => {
    const proposed = gameState?.proposedCards?.[gameState.proposedCards.length - 1];
    if (!proposed) return null;
    return (
      <span className="raub-vote-suit" style={{ color: SUIT_COLORS[proposed.suit] }}>
        {SUIT_ICONS[proposed.suit]} {proposed.suit}
      </span>
    );
  };

  const renderDealerDecision = () => {
    if (gameState?.gamePhase !== 'dealer-decision') return null;

    const proposed = gameState.proposedCards[gameState.proposedCards.length - 1];
    const dealerName = gameState.players[gameState.dealerIndex]?.name || 'Dealer';

    const handleVoteCardTap = () => {
      if (!amDealer || !isMyTurn) return;
      const now = Date.now();
      if (now - (voteCardTapRef.current || 0) < 400) {
        handleDealerDecision('raub');
      }
      voteCardTapRef.current = now;
    };

    return (
      <div className="raub-vote-panel raub-vote-panel--compact">
        <h4 className="raub-vote-title">Trump vote</h4>
        <p className="raub-vote-sub raub-vote-sub--desktop">
          Flipped card proposes {renderProposedSuitLabel()} as trump
        </p>
        {proposed && (
          <img
            src={getCardImage(proposed)}
            alt="Proposed trump"
            className="raub-vote-card"
            onClick={handleVoteCardTap}
            title="Double-tap to Raub"
          />
        )}
        {amDealer && isMyTurn ? (
          <>
            <p className="raub-vote-hint raub-vote-hint--desktop">
              {proposed?.rank === 7
                ? 'This is a 7 — raub it and you only need to win 1 trick.'
                : 'Accept this suit as trump (Raub — you must win 2 tricks and take this card), or pass to the next player.'}
            </p>
            <p className="raub-vote-hint raub-vote-hint--mobile">Double-tap the card to call, or use the buttons below.</p>
            <div className="raub-inline-buttons">
              <button className="raub-action-btn primary" onClick={() => handleDealerDecision('raub')}>
                <span className="raub-btn-short">Call</span>
                <span className="raub-btn-long">Raub! ({proposed?.suit}{proposed?.rank === 7 ? ' — 1 trick' : ''})</span>
              </button>
              <button className="raub-action-btn secondary" onClick={() => handleDealerDecision('pass')}>
                <span className="raub-btn-short">Pass</span>
                <span className="raub-btn-long">Dalje (pass)</span>
              </button>
            </div>
            {musAvailable && (
              <>
                <button className="raub-action-btn mus raub-vote-mus--desktop" onClick={() => handleDealerDecision('mus')}>
                  MUS (blind trump)
                </button>
                <p className="raub-vote-hint raub-vote-hint--desktop">
                  MUS: trump is dealt face-down and hidden until you start the match. No card exchange, you must win 2 tricks.
                </p>
              </>
            )}
          </>
        ) : (
          <p className="raub-waiting-text">Waiting for dealer <strong>{dealerName}</strong> to decide…</p>
        )}
      </div>
    );
  };

  const renderAdutDetermination = () => {
    if (gameState?.gamePhase !== 'adut-determination') return null;

    const askingPlayerName = askingPlayer?.name || 'Unknown';
    const proposed = gameState.proposedCards[gameState.proposedCards.length - 1];

    const handleVoteCardTap = () => {
      if (!isMyAsk) return;
      const now = Date.now();
      if (now - (voteCardTapRef.current || 0) < 400) {
        handleBid('raub');
      }
      voteCardTapRef.current = now;
    };

    return (
      <div className="raub-vote-panel raub-vote-panel--compact">
        <h4 className="raub-vote-title">
          Trump vote — round {gameState.currentProposalRound + 1}
        </h4>
        <p className="raub-vote-sub raub-vote-sub--desktop">
          Proposed trump: {renderProposedSuitLabel()}
        </p>
        {proposed && (
          <img
            src={getCardImage(proposed)}
            alt="Proposed trump"
            className="raub-vote-card"
            onClick={handleVoteCardTap}
            title="Double-tap to Raub"
          />
        )}
        {isMyAsk ? (
          <>
            <p className="raub-vote-hint raub-vote-hint--desktop">
              Does this suit suit you? Raub to make it trump (you must win 2 tricks), or pass.
            </p>
            <p className="raub-vote-hint raub-vote-hint--mobile">Double-tap the card to call, or use the buttons below.</p>
            <div className="raub-inline-buttons">
              <button className="raub-action-btn primary" onClick={() => handleBid('raub')}>
                <span className="raub-btn-short">Call</span>
                <span className="raub-btn-long">Raub! ({proposed?.suit})</span>
              </button>
              <button className="raub-action-btn secondary" onClick={() => handleBid('pass')}>
                <span className="raub-btn-short">Pass</span>
                <span className="raub-btn-long">Dalje (pass)</span>
              </button>
            </div>
          </>
        ) : (
          <p className="raub-waiting-text">Waiting for <strong>{askingPlayerName}</strong> to decide…</p>
        )}
      </div>
    );
  };

  const renderMusReady = () => {
    if (gameState?.gamePhase !== 'mus-ready') return null;
    const dealerName = gameState.players[gameState.dealerIndex]?.name || 'Dealer';
    return (
      <div className="raub-vote-panel">
        <h4 className="raub-vote-title">MUS — trump is hidden</h4>
        <p className="raub-vote-sub">The trump card is face-down. Nobody knows the adut until the match starts.</p>
        <img src="/assets/back.png" alt="Hidden trump" className="raub-vote-card" />
        {amDealer ? (
          <>
            <p className="raub-vote-hint">
              Start the match: the trump is revealed to everyone, you take that card and discard one. You must win 2 tricks. No card exchange.
            </p>
            <div className="raub-inline-buttons">
              <button className="raub-action-btn primary" onClick={handleRevealMus}>
                Reveal trump & start
              </button>
            </div>
          </>
        ) : (
          <p className="raub-waiting-text">Waiting for dealer <strong>{dealerName}</strong> to start the MUS match…</p>
        )}
      </div>
    );
  };

  const renderMusDiscard = () => {
    if (gameState?.gamePhase !== 'mus-discard') return null;
    return (
      <div className="raub-vote-panel">
        <h4 className="raub-vote-title">MUS — discard one card</h4>
        {gameState.trumpSuit && (
          <p className="raub-vote-sub">
            Trump revealed: {renderProposedSuitLabel() || (
              <span className="raub-vote-suit" style={{ color: SUIT_COLORS[gameState.trumpSuit] }}>
                {SUIT_ICONS[gameState.trumpSuit]} {gameState.trumpSuit}
              </span>
            )}
          </p>
        )}
        {isAwaitingMyDiscard ? (
          <>
            <p className="raub-vote-hint">Pick exactly one card from your hand to discard, then confirm.</p>
            <div className="raub-inline-buttons">
              <button
                className="raub-action-btn primary"
                onClick={handleConfirmDiscard}
                disabled={selectedDiscardIndices.length !== 1}
              >
                Confirm discard ({selectedDiscardIndices.length}/1)
              </button>
            </div>
          </>
        ) : (
          <p className="raub-waiting-text">Dealer is discarding…</p>
        )}
      </div>
    );
  };

  const renderCardExchange = () => {
    if (gameState?.gamePhase !== 'card-exchange') return null;

    const myDiscardCount = gameState.discardCounts[user?.id || ''];
    const hasSubmitted = myDiscardCount !== null && myDiscardCount !== undefined;

    return (
      <div className="raub-exchange-panel">
        <div className="raub-exchange-header">
          <h3 className="raub-exchange-title">Card Exchange</h3>
          {renderTrumpIndicator()}
          {gameState.raubPlayerId && (
            <div className="raub-rauber-info">
              Rauber: <strong>{gameState.players.find(p => p.id === gameState.raubPlayerId)?.name}</strong>
              {gameState.raubObligationTricks > 0 && (
                <span> (must win {gameState.raubObligationTricks} tricks)</span>
              )}
            </div>
          )}
        </div>

        {/* Exchange status for all players */}
        <div className="raub-exchange-status">
          {gameState.players.map(p => {
            const dc = gameState.discardCounts[p.id];
            return (
              <div key={p.id} className={`raub-exchange-player-status ${p.id === user?.id ? 'is-me' : ''}`}>
                <span>{p.name}</span>
                <span className="raub-exchange-count">
                  {dc === null || dc === undefined ? 'Choosing...' : `Done (${dc})`}
                </span>
              </div>
            );
          })}
        </div>

        {/* Face-up exchange card */}
        {gameState.faceUpExchangeCard && (
          <div className="raub-faceup-exchange">
            <span>Face-up card:</span>
            <img
              src={getCardImage(gameState.faceUpExchangeCard)}
              alt="Face-up exchange"
              className="raub-faceup-card-img"
            />
          </div>
        )}

        {/* Simple discard UI: click cards in your hand, then confirm */}
        {!hasSubmitted && !isAwaitingMyDiscard && (
          <div className="raub-discard-simple">
            <p>Click cards to discard, then confirm. (Click again to deselect)</p>
            <div className="raub-inline-buttons">
              <button
                className="raub-action-btn secondary"
                onClick={() => setSelectedDiscardIndices([])}
                disabled={selectedDiscardIndices.length === 0}
              >
                Change
              </button>
              <button
                className="raub-action-btn primary"
                onClick={handleConfirmDiscard}
              >
                Confirm ({selectedDiscardIndices.length} selected)
              </button>
            </div>
          </div>
        )}

        {/* Awaiting extra discard after receiving new cards */}
        {isAwaitingMyDiscard && (
          <div className="raub-discard-simple">
            <p style={{ color: '#FFD700' }}>
              Select extra card(s) to discard.
            </p>
            <div className="raub-inline-buttons">
              <button
                className="raub-action-btn secondary"
                onClick={() => setSelectedDiscardIndices([])}
                disabled={selectedDiscardIndices.length === 0}
              >
                Change
              </button>
              <button
                className="raub-action-btn primary"
                onClick={handleConfirmDiscard}
                disabled={selectedDiscardIndices.length === 0}
              >
                Confirm ({selectedDiscardIndices.length} selected)
              </button>
            </div>
          </div>
        )}

        {hasSubmitted && !isAwaitingMyDiscard && (
          <p className="raub-waiting-text">Waiting for others...</p>
        )}
      </div>
    );
  };

  const renderPlayingTable = () => {
    if (!gameState) return null;
    const orderedPlayers = getOrderedPlayers();
    const total = orderedPlayers.length;

    return (
      <div className="playing-table">
        {/* Center area: trick + trump + rauber info */}
        <div className="table-center raub-playing-center">
          {renderTrumpIndicator()}

          {gameState.raubPlayerId && (
            <div className="raub-rauber-badge">
              Rauber: {gameState.players.find(p => p.id === gameState.raubPlayerId)?.name}
            </div>
          )}

          {gameState.isRefa && (
            <div className="raub-refa-badge">
              REFA x{gameState.refaMultiplier}
            </div>
          )}

          <div className="raub-trick-number">
            Trick {gameState.trickNumber}/{Math.max(gameState.completedTricks.length + 1, gameState.trickNumber)}
          </div>

          {renderCurrentTrick()}

          {gameState.currentTrick?.winnerId && (
            <div className="raub-trick-winner">
              Won by: {gameState.players.find(p => p.id === gameState.currentTrick!.winnerId)?.name}
            </div>
          )}
        </div>

        {/* Player positions around table */}
        {orderedPlayers.map((player, visualIdx) => {
          const isMe = player.id === user?.id;
          const realIdx = gameState.players.findIndex(p => p.id === player.id);
          const isTurn = gameState.currentPlayerIndex === realIdx;
          const posStyle = getPositionStyle(visualIdx, total);

          return (
            <div
              key={player.id}
              className={`raub-table-seat ${isMe ? 'is-me' : ''} ${isTurn ? 'is-turn' : ''}`}
              style={{ position: 'absolute', ...posStyle }}
            >
              <div className="raub-seat-info">
                <span className="raub-seat-name-label">{player.name}</span>
                {player.isDealer && <span className="raub-dealer-badge-small">D</span>}
                <span className="raub-tricks-badge">{player.tricksWon} tricks</span>
              </div>

              {isMe ? (
                renderPlayerHand()
              ) : (
                <div className="opponent-cards">
                  {player.cards.map((_, ci) => (
                    <img
                      key={ci}
                      src="/assets/back.png"
                      alt="Hidden card"
                      className="opponent-card"
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderHandFinished = () => {
    if (gameState?.gamePhase !== 'hand-finished') return null;

    return (
      <div className="raub-inline-action raub-results-inline">
        <h4 className="raub-inline-title">Hand Finished!</h4>

        {gameState.isRefa && (
          <div className="raub-refa-result">
            REFA! x{gameState.refaMultiplier}
          </div>
        )}

        <div className="raub-results-grid">
          {gameState.players.map(p => (
            <div
              key={p.id}
              className={`raub-result-row ${p.id === gameState.raubPlayerId ? 'is-rauber' : ''}`}
            >
              <span className="raub-result-name">{p.name}</span>
              <span className="raub-result-tricks">{p.tricksWon} tricks</span>
              <span className={`raub-result-score ${(gameState.playerScores[p.id] ?? 0) < 0 ? 'negative' : ''}`}>
                {gameState.playerScores[p.id] ?? 0} pts
              </span>
            </div>
          ))}
        </div>

        {isHost && (
          <button className="raub-action-btn primary" onClick={handleStartNewHand} style={{ marginTop: '12px' }}>
            Next Hand
          </button>
        )}
        {!isHost && (
          <p className="raub-waiting-text">Waiting for host...</p>
        )}
      </div>
    );
  };

  const renderGameOver = () => {
    if (gameState?.gamePhase !== 'game-over') return null;

    const winnerPlayer = gameState.winner
      ? gameState.players.find(p => p.id === gameState.winner)
      : null;

    const sortedPlayers = [...gameState.players].sort(
      (a, b) => (gameState.playerScores[b.id] ?? 0) - (gameState.playerScores[a.id] ?? 0)
    );

    return (
      <div className="raub-inline-action raub-results-inline">
        <h4 className="raub-inline-title">Game Over!</h4>

        {winnerPlayer && (
          <div className="raub-winner-banner">
            Winner: {winnerPlayer.name}
          </div>
        )}

        <div className="raub-final-standings">
          {sortedPlayers.map((p, rank) => (
            <div key={p.id} className="raub-result-row">
              <span className="raub-rank">#{rank + 1}</span>
              <span className="raub-result-name">{p.name}</span>
              <span className={`raub-result-score ${(gameState.playerScores[p.id] ?? 0) < 0 ? 'negative' : ''}`}>
                {gameState.playerScores[p.id] ?? 0} pts
              </span>
            </div>
          ))}
        </div>

        <button
          className="raub-action-btn primary"
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: '12px' }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  };

  const renderGameTable = () => {
    if (!game) return null;

    if (game.status === 'waiting') {
      return renderWaitingTable();
    }

    if (!gameState) return null;

    const phase = gameState.gamePhase;
    if (phase === 'playing') return renderPlayingTable();
    if (phase === 'card-exchange') {
      // Show the table with players and their cards, plus exchange panel below
      const orderedPlayers = getOrderedPlayers();
      const total = orderedPlayers.length;

      return (
        <div className="playing-table">
          <div className="table-center raub-playing-center">
            {renderTrumpIndicator()}
            {gameState.raubPlayerId && (
              <div className="raub-rauber-badge">
                Rauber: {gameState.players.find(p => p.id === gameState.raubPlayerId)?.name}
              </div>
            )}
            {gameState.raubedCard && (
              <div style={{ marginTop: '10px' }}>
                <span style={{ color: '#FFD700', fontSize: '13px' }}>Raubed card:</span>
                <img
                  src={getCardImage(gameState.raubedCard)}
                  alt="Raubed card"
                  style={{ width: '80px', height: '116px', borderRadius: '6px', marginLeft: '8px', verticalAlign: 'middle' }}
                />
              </div>
            )}
          </div>

          {orderedPlayers.map((player, visualIdx) => {
            const isMe = player.id === user?.id;
            const posStyle = getPositionStyle(visualIdx, total);

            return (
              <div
                key={player.id}
                className={`raub-table-seat ${isMe ? 'is-me' : ''}`}
                style={{ position: 'absolute', ...posStyle }}
              >
                <div className="raub-seat-info">
                  <span className="raub-seat-name-label">{player.name}</span>
                  {player.isDealer && <span className="raub-dealer-badge-small">D</span>}
                </div>

                {isMe ? (
                  renderPlayerHand()
                ) : (
                  <div className="opponent-cards">
                    {player.cards.map((_, ci) => (
                      <img
                        key={ci}
                        src="/assets/back.png"
                        alt="Hidden card"
                        className="opponent-card"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // For dealer-decision / adut-determination phases, show a basic table
    const orderedPlayers = getOrderedPlayers();
    const total = orderedPlayers.length;
    return (
      <div className="playing-table">
        <div className="table-center raub-playing-center">
          {renderDealerDecision()}
          {renderAdutDetermination()}
          {renderMusReady()}
          {renderMusDiscard()}
          {renderHandFinished()}
          {renderGameOver()}
        </div>
        {orderedPlayers.map((player, visualIdx) => {
          const isMe = player.id === user?.id;
          const posStyle = getPositionStyle(visualIdx, total);
          return (
            <div
              key={player.id}
              className={`raub-table-seat ${isMe ? 'is-me' : ''}`}
              style={{ position: 'absolute', ...posStyle }}
            >
              <div className="raub-seat-info">
                <span className="raub-seat-name-label">{player.name}</span>
                {player.isDealer && <span className="raub-dealer-badge-small">D</span>}
              </div>
              {isMe ? (
                renderPlayerHand()
              ) : (
                <div className="opponent-cards">
                  {player.cards.map((_, ci) => (
                    <img key={ci} src="/assets/back.png" alt="Hidden card" className="opponent-card" />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Loading / error states ────────────────────────────────────────

  if (loading) {
    return (
      <div className="game-room-layout">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Raub game...</p>
        </div>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="game-room-layout">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="game-room-layout">
        <div className="error-container">
          <p>Game not found</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────

  return (
    <div className="game-room-layout">
      {/* Error toast */}
      {error && (
        <div className="raub-error-toast" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {/* Floating mobile controls */}
      <button
        className="game-mobile-burger"
        aria-label="Open menu"
        onClick={() => setLeftPanelCollapsed(false)}
      >
        ☰
      </button>
      <button
        className="game-mobile-info"
        aria-label="Open game details"
        onClick={() => setRightPanelCollapsed(false)}
      >
        📊
      </button>

      {/* Left Panel */}
      <div className={`game-left-panel ${leftPanelCollapsed ? 'collapsed' : ''}`}>
        <div className="panel-header">
          <button
            className="collapse-btn"
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            title={leftPanelCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {leftPanelCollapsed ? '☰' : '✕'}
          </button>
          {!leftPanelCollapsed && <h3>Raub</h3>}
        </div>

        {!leftPanelCollapsed && (
          <div className="panel-content">
            <button className="nav-button" onClick={handleLeaveRoom}>
              <span>🏠</span><span>Dashboard</span>
            </button>

            <div className="nav-section">
              <h4>Game Actions</h4>
              {game.status === 'waiting' && isHost && (
                <button
                  className="nav-button action-btn"
                  onClick={() => {
                    if (game.players.length < 2) {
                      setError('Need at least 2 players');
                      return;
                    }
                    setShowCardCuttingModal(true);
                  }}
                  disabled={game.players.length < 2}
                >
                  Start Game {game.players.length < 2 ? `(${game.players.length}/2)` : ''}
                </button>
              )}
            </div>

            <div className="nav-section">
              <h4>Players</h4>
              <div className="players-list">
                {game.players.map((playerId, index) => {
                  const player = gameState?.players.find(p => p.id === playerId);
                  const pu = playerUsers[playerId];
                  const displayName = pu?.firstName
                    ? `${pu.firstName} ${pu.lastName || ''}`.trim()
                    : player?.name || `Player ${index + 1}`;
                  return (
                    <div key={playerId} className="player-item">
                      <span className="player-name">
                        {displayName}
                      </span>
                      <div className="player-badges">
                        {player?.isDealer && <span className="dealer-badge">D</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {game.inviteCode && (
              <div className="nav-section">
                <h4>Invite Code</h4>
                <div className="player-item">
                  <span style={{ fontFamily: 'monospace', letterSpacing: '2px' }}>
                    {game.inviteCode}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Central Game Table */}
      <div className="game-center-area">
        {gameState?.gamePhase === 'card-exchange' && (
          <div className="raub-exchange-above-table">
            {renderCardExchange()}
          </div>
        )}
        <div className="game-table-container">
          <div className="game-table">
            {renderGameTable()}
          </div>
        </div>

        {/* Board action bar - host start always visible on the table */}
        {game.status === 'waiting' && isHost && (
          <div className="board-action-bar">
            <button
              className="board-action-btn start"
              onClick={() => {
                if (game.players.length < 2) {
                  setError('Need at least 2 players');
                  return;
                }
                setShowCardCuttingModal(true);
              }}
              disabled={game.players.length < 2}
            >
              ▶️ Start Game{game.players.length < 2 ? ` (${game.players.length}/2)` : ''}
            </button>
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div className={`game-right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
        <div className="panel-header">
          <button
            className="collapse-btn"
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            title={rightPanelCollapsed ? 'Expand details' : 'Collapse details'}
          >
            {rightPanelCollapsed ? '📊' : '✕'}
          </button>
          {!rightPanelCollapsed && <h3>Game Details</h3>}
        </div>

        {!rightPanelCollapsed && (
          <div className="panel-content">
            {/* Status */}
            <div className="info-section">
              <h4>Status</h4>
              <div className="status-info">
                <div className="status-item">
                  <span>Game:</span>
                  <span className={`status-badge ${game.status}`}>{game.status}</span>
                </div>
                {gameState && (
                  <div className="status-item">
                    <span>Phase:</span>
                    <span className="phase-badge">{gameState.gamePhase}</span>
                  </div>
                )}
                {gameState && (
                  <div className="status-item">
                    <span>Match:</span>
                    <span>{gameState.currentMatch}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Trump */}
            {gameState?.trumpSuit && (
              <div className="info-section">
                <h4>Trump (Adut)</h4>
                <div className="trump-info">
                  <span style={{ color: SUIT_COLORS[gameState.trumpSuit], fontSize: '24px' }}>
                    {SUIT_ICONS[gameState.trumpSuit]}
                  </span>
                  <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {gameState.trumpSuit}
                  </span>
                </div>
              </div>
            )}

            {/* Rauber info */}
            {gameState?.raubPlayerId && (
              <div className="info-section">
                <h4>Rauber</h4>
                <div className="status-info">
                  <div className="status-item">
                    <span>Player:</span>
                    <span style={{ color: '#FFD700' }}>
                      {gameState.players.find(p => p.id === gameState.raubPlayerId)?.name}
                    </span>
                  </div>
                  {gameState.raubObligationTricks > 0 && (
                    <div className="status-item">
                      <span>Must win:</span>
                      <span>{gameState.raubObligationTricks} tricks</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scores */}
            {gameState && gameState.gamePhase !== 'waiting' && (
              <div className="info-section">
                <h4>Scores</h4>
                <div className="scores-list">
                  {gameState.players.map(player => {
                    const score = gameState.playerScores[player.id] ?? 0;
                    return (
                      <div key={player.id} className="score-item">
                        <span className="player-name">
                          {player.name}
                          {player.id === user?.id && ' (You)'}
                        </span>
                        <span className={`score ${score <= 0 ? 'losing' : ''}`}>
                          {score} pts
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tricks won (during playing) */}
            {gameState && (gameState.gamePhase === 'playing' || gameState.gamePhase === 'hand-finished') && (
              <div className="info-section">
                <h4>Tricks Won</h4>
                <div className="scores-list">
                  {gameState.players.map(player => (
                    <div key={player.id} className="score-item">
                      <span className="player-name">{player.name}</span>
                      <span className="score">{player.tricksWon}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Refa indicator */}
            {gameState?.isRefa && (
              <div className="info-section">
                <h4>Refa</h4>
                <div className="status-info">
                  <div className="status-item">
                    <span>Count:</span>
                    <span>{gameState.refaCount}</span>
                  </div>
                  <div className="status-item">
                    <span>Multiplier:</span>
                    <span style={{ color: '#f44336', fontWeight: 'bold' }}>x{gameState.refaMultiplier}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="info-section">
              <h4>Controls</h4>
              <div className="controls">
                <button className="control-btn" onClick={() => navigate('/dashboard')}>
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card Cutting Modal */}
      <CardCuttingModal
        isOpen={showCardCuttingModal}
        onCut={handleCardCut}
        onCancel={() => setShowCardCuttingModal(false)}
      />
    </div>
  );
};

export default RaubGameRoom;
